import { Link } from "react-router-dom"
import { alpha, keyframes, styled } from "@mui/material/styles"
import TextField from "@mui/material/TextField"
import {
  Badge,
  Accordion as MUIAccordion,
  AccordionDetails as MUIAccordionDetails,
  AccordionSummary as MUIAccordionSummary,
  Tab as MUITab,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"

import type { LinkProps } from "react-router-dom"
import type { TabProps } from "@mui/material"

import type { AccordionProps, DefaultStyledProps } from "types/styled.types"

import { getAccordionStateColor } from "helpers/utils/styles"

export const AutocompleteInput = styled(TextField)<DefaultStyledProps>(({ theme }) => ({
  input: {
    color: theme.palette.grey[100],
  },
  label: {
    color: theme.palette.grey[100],
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: theme.palette.grey[100],
    },
    "&:hover fieldset": {
      borderColor: theme.palette.grey[100],
    },
  },
}))

export const StyledDataGrid = styled(DataGrid)<DefaultStyledProps>(({ theme }) => ({
  // Borderless like mui-datatables' tables; the screens that framed one pass the border in sx.
  border: "none",
  backgroundColor: theme.palette.background.paper,
  // An empty table shows its message in a three-line band, not a two-row-high backdrop: room for
  // ActivityPanel's two lines, and a third when they wrap on a narrow screen, with 8px around them.
  "--DataGrid-overlayHeight": "70px",
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
  },
  // v8 tints the header with a white mix in dark mode; keep it the table's color. Doubled
  // class to outrank the variables the grid injects in its own style tag.
  "&&": {
    "--DataGrid-t-header-background-base": theme.palette.background.paper,
  },
  "--DataGrid-rowBorderColor": theme.palette.neutral[300],
  "& .MuiDataGrid-row:hover": {
    backgroundColor: alpha(theme.palette.neutral[300], 0.5),
  },
  // A title that does not fit wraps onto a second line, as mui-datatables' did, instead of
  // being cut with an ellipsis.
  "& .MuiDataGrid-columnHeaderTitle": {
    ...theme.typography.overlineDense,
    whiteSpace: "normal",
  },
  "& .MuiDataGrid-columnSeparator": {
    display: "none",
  },
  // Wraps for the stacked toolbar below md (see DataGridToolbar). The grid gives the toolbar a 1px
  // flex basis that holds it at its min height, so the extra lines would overflow.
  "& .MuiDataGrid-toolbar": {
    borderBottom: "none",
    minHeight: "50px",
    flex: "0 0 auto",
    flexWrap: "wrap",
    gap: "8px",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "24px",
    paddingRight: "24px",
    // Stacked, the title sits on the top edge rather than centered in the 50px.
    [theme.breakpoints.down("md")]: {
      paddingTop: "16px",
      paddingBottom: "8px",
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  // Not the empty filler cell that closes each row: padding it widens every row by 32px.
  "& .MuiDataGrid-cell:not(.MuiDataGrid-cellEmpty), & .MuiDataGrid-columnHeader": {
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  // Content centered in the fixed-height row. The grid sets the cell's line-height to the row's
  // height, which made every line of a two-line cell as tall as the whole row.
  "& .MuiDataGrid-cell:not(.MuiDataGrid-cellEmpty)": {
    display: "flex",
    alignItems: "center",
    lineHeight: "inherit",
  },
  // Lets the content shrink to the cell, so long text is cut instead of overflowing it.
  "& .MuiDataGrid-cell > *": {
    minWidth: 0,
  },
  "& .MuiDataGrid-footerContainer": {
    borderTopColor: theme.palette.neutral[300],
    minHeight: "49px",
  },
  // 50px with the footer's border, like the old pagination bar.
  "& .MuiDataGrid-footerContainer .MuiTablePagination-toolbar": {
    minHeight: "49px",
  },
  // mui-datatables' checkbox cell: MUI's padding="checkbox" (4px left) around a checkbox padded
  // 0 1rem, so it adds no height to the row.
  "&& .MuiDataGrid-cellCheckbox, && .MuiDataGrid-columnHeaderCheckbox": {
    paddingLeft: "4px",
    paddingRight: 0,
  },
  "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root":
    {
      padding: "0 1rem",
    },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
    outline: "none",
  },
  // No outline on click, as the old tables, but keyboard navigation still shows where it is.
  "& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}))

const filler = keyframes`
    0% { transform: scale(0.9); opacity: 0.3; }
    100% { transform: scale(1); opacity: 1;}
`
export const SvgLogo = styled("img")(() => ({
  position: "absolute",
  height: "10vh",
  opacity: 0.2,
  animation: `${filler} 1s linear infinite`,
  animationDirection: "alternate",

  "&#progress.start": {
    animation: `${filler} 1.2s cubic-bezier(0.3, 0.3, 0.3, 1) 3s both`,
  },
}))

const Tab = styled(MUITab)(({ theme }) => ({
  textTransform: "none",
  minHeight: 40,
  padding: "6px 16px",
  marginRight: 4,
  borderRadius: 8,
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  "& .MuiTab-iconWrapper": {
    marginRight: 6,
  },
}))

const _LinkTab: React.ComponentType<TabProps & LinkProps> = (props) => {
  const TabWithComponent = Tab as React.ComponentType<
    TabProps &
      LinkProps & {
        component: React.ForwardRefExoticComponent<
          LinkProps & React.RefAttributes<HTMLAnchorElement>
        >
      }
  >
  return <TabWithComponent {...props} component={Link} />
}

export const LinkTab: React.ComponentType<TabProps & LinkProps> = _LinkTab

export const Accordion = styled(MUIAccordion, {
  shouldForwardProp: (prop) => prop !== "state",
})<AccordionProps>(({ theme, state }) => ({
  backgroundColor: "transparent",
  borderColor: alpha(getAccordionStateColor(state, theme), 0.5),
}))

export const AccordionSummary = styled(MUIAccordionSummary, {
  shouldForwardProp: (prop) => prop !== "state",
})<AccordionProps>(({ theme, state }) => ({
  backgroundColor: alpha(getAccordionStateColor(state, theme), 0.05),
}))

export const AccordionDetails = styled(MUIAccordionDetails, {
  shouldForwardProp: (prop) => prop !== "state",
})<AccordionProps>(({ theme, state }) => ({
  borderTop: `1px solid ${alpha(getAccordionStateColor(state, theme), 0.5)}`,
}))

const rainbowAnimation = keyframes`
  0% { background-position: 0% 35%; }
  50% { background-position: 100% 35%; }
  100% { background-position: 0% 35%; }
`
export const AnimatedBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    background: `linear-gradient(45deg,
      ${theme.palette.primary.main},
      ${theme.palette.secondary.main},
      ${theme.palette.warning.main},
      ${theme.palette.secondary.main},
      ${theme.palette.primary.main}
    )`,
    backgroundSize: "300% 300%",
    animation: `${rainbowAnimation} 3s ease infinite`,
    color: theme.palette.neutral[100],
    fontWeight: 900,
    fontSize: "0.6rem",
    padding: "0 4px",
    height: 15,
    minWidth: 18,
    right: 1,
    top: 7,
  },
}))
